import { Badge } from '@/admin/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import type { TraceRequest } from '../../api';
import {
  compactStatusLabel,
  formatDateTime,
  formatMs,
  shortId,
  statusVariant,
} from '../../format';
import type { TraceRequestActivitySummary } from '../../trace/presentation';

export function RequestList({
  requests,
  requestActivity,
  selectedRequestID,
  showInternal,
  showLifecycleOnly,
  onToggleInternal,
  onToggleLifecycleOnly,
  onSelect,
}: {
  requests: TraceRequest[];
  requestActivity: Map<string, TraceRequestActivitySummary>;
  selectedRequestID: string;
  showInternal: boolean;
  showLifecycleOnly: boolean;
  onToggleInternal: (value: boolean) => void;
  onToggleLifecycleOnly: (value: boolean) => void;
  onSelect: (requestID: string) => void;
}) {
  return (
    <Card className="min-h-0 xl:flex xl:flex-col">
      <CardHeader className="space-y-3 xl:shrink-0">
        <CardTitle className="text-base">Requests</CardTitle>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={event => onToggleInternal(event.currentTarget.checked)}
          />
          Show internal requests
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showLifecycleOnly}
            onChange={event =>
              onToggleLifecycleOnly(event.currentTarget.checked)
            }
          />
          Show lifecycle-only requests
        </label>
      </CardHeader>
      <CardContent className="space-y-2 xl:min-h-0 xl:flex-1 xl:overflow-auto">
        {requests.length === 0 ? (
          <div className="text-sm text-muted-foreground">No requests.</div>
        ) : (
          requests.map(request => (
            <button
              key={request.request_id}
              type="button"
              className={`w-full rounded-md border p-3 text-left text-sm transition-colors hover:bg-muted ${
                request.request_id === selectedRequestID
                  ? 'border-primary bg-muted'
                  : ''
              }`}
              onClick={() => onSelect(request.request_id)}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 break-words font-medium leading-snug">
                  {request.label || shortId(request.request_id)}
                </span>
                <Badge
                  variant={statusVariant(request.status)}
                  className="shrink-0 whitespace-nowrap px-1.5 py-0 text-[10px] font-medium"
                  title={request.status}
                >
                  {compactStatusLabel(request.status)}
                </Badge>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {formatDateTime(request.started_at)} · total{' '}
                {formatMs(request.timing.total_ms)}
              </div>
              {request.internal ? (
                <Badge variant="outline" className="mt-2">
                  internal
                </Badge>
              ) : null}
              <RequestActivityBadge
                summary={requestActivity.get(request.request_id)}
              />
            </button>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function RequestActivityBadge({
  summary,
}: {
  summary?: TraceRequestActivitySummary;
}) {
  if (!summary) return null;
  if (summary.lifecycleOnly) {
    return (
      <Badge variant="outline" className="mt-2">
        lifecycle only
      </Badge>
    );
  }
  if (summary.primaryCount === 0 && summary.auxiliaryCount > 0) {
    return (
      <Badge variant="outline" className="mt-2">
        auxiliary only
      </Badge>
    );
  }
  return null;
}
